import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNotify, useRecordContext, useRefresh } from "react-admin";
import { useState } from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import EmailIcon from "@mui/icons-material/Email";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

import {
  BookingRequestAction,
  BookingRequestRecord,
  respondToBookingRequest,
} from "../services/bookingRequests";

const DIALOG_COPY: Record<
  BookingRequestAction,
  { title: string; submitLabel: string; intro: string }
> = {
  approve: {
    title: "Approve & Schedule",
    submitLabel: "Approve & Send Confirmation",
    intro:
      "Approving sends the requestor a confirmation email with the scheduled date and time.",
  },
  contact: {
    title: "Contact Requestor",
    submitLabel: "Send Email",
    intro: "Your message is emailed to the requestor from the club address.",
  },
  reject: {
    title: "Reject Request",
    submitLabel: "Reject & Notify",
    intro:
      "Rejecting sends the requestor a polite email letting them know we can't accommodate the request.",
  },
};

export const BookingRequestActions = () => {
  const record = useRecordContext<BookingRequestRecord>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [action, setAction] = useState<BookingRequestAction | null>(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!record) return null;

  const openDialog = (nextAction: BookingRequestAction) => {
    setSubject(`Regarding Your ${record.requestType} Request - The Cola`);
    setMessage("");
    setScheduledDate(record.scheduledDate || record.preferredDate || "");
    setScheduledTime(record.scheduledTime || record.preferredTime || "");
    setAction(nextAction);
  };

  const closeDialog = () => {
    if (!submitting) setAction(null);
  };

  const handleSubmit = async () => {
    if (!action) return;
    setSubmitting(true);
    try {
      const result = await respondToBookingRequest({
        requestId: String(record.id),
        action,
        subject: action === "contact" ? subject : undefined,
        message: message || undefined,
        scheduledDate: action === "approve" ? scheduledDate : undefined,
        scheduledTime: action === "approve" ? scheduledTime : undefined,
      });
      notify(result.message || "Email sent", { type: "success" });
      setAction(null);
      refresh();
    } catch (error: any) {
      notify(error?.message || "Failed to send email", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisabled =
    submitting ||
    (action === "contact" && (!subject.trim() || !message.trim())) ||
    (action === "approve" && !scheduledDate);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" gutterBottom>
        Respond
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Button
          variant="contained"
          color="success"
          startIcon={<EventAvailableIcon />}
          onClick={() => openDialog("approve")}
        >
          Approve & Schedule
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<EmailIcon />}
          onClick={() => openDialog("contact")}
        >
          Contact Requestor
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={() => openDialog("reject")}
        >
          Reject
        </Button>
      </Stack>

      <Dialog open={!!action} onClose={closeDialog} fullWidth maxWidth="sm">
        {action && (
          <>
            <DialogTitle>{DIALOG_COPY[action].title}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Alert severity="info">
                  {DIALOG_COPY[action].intro} It will be sent to{" "}
                  <strong>{record.email}</strong>.
                </Alert>

                {action === "approve" && (
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Scheduled Date"
                      type="date"
                      required
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                    />
                    <TextField
                      label="Scheduled Time"
                      type="time"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                    />
                  </Stack>
                )}

                {action === "contact" && (
                  <TextField
                    label="Subject"
                    required
                    fullWidth
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                )}

                <TextField
                  label={
                    action === "contact"
                      ? "Message"
                      : "Additional message (optional)"
                  }
                  required={action === "contact"}
                  multiline
                  rows={4}
                  fullWidth
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color={
                  action === "reject"
                    ? "error"
                    : action === "approve"
                    ? "success"
                    : "primary"
                }
                onClick={handleSubmit}
                disabled={submitDisabled}
              >
                {submitting ? "Sending..." : DIALOG_COPY[action].submitLabel}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default BookingRequestActions;
